import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  useCampaign,
  useUpdateCampaign,
  useUpdateCampaignMailboxes,
  useUpdateCampaignSmsNumber,
} from '../../api/campaigns.api';
import { useOrgSettings } from '../../api/org-settings.api';
import { useSmtpMailboxes } from '../../api/smtp-mailboxes.api';
import { useSmsAccounts } from '../../api/sms-accounts.api';
import { useOrgUsers } from '../../api/org-users.api';
import { findOwner } from '../../lib/people';
import { useToast } from '../../components/ui/Toast';
import {
  FormCard,
  FormHeader,
  WizardLayout,
  WizardFooter,
  enrollmentMode,
  stepInfo,
  stepPath,
} from './builder-shell';
import { TemplateInputsCard } from './template-inputs/TemplateInputsCard';

/* ── Screen 15 — Campaign builder · Settings step ── */

export function CampaignSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: campaign, isLoading } = useCampaign(id);
  const { data: orgSettings } = useOrgSettings();
  const activeSmsProvider = orgSettings?.data?.active_sms_provider ?? 'TWILIO';

  if (!id) return <Navigate to="/campaigns/new" replace />;
  if (isLoading || !campaign) return <LoadingShell />;
  // After launch, the wizard is read-only; users edit via the detail page.
  if (campaign.status !== 'DRAFT') return <Navigate to={`/campaigns/${id}`} replace />;

  const info = stepInfo('settings', enrollmentMode(campaign));

  return (
    <WizardLayout step="settings" campaignId={id} campaign={campaign}>
      <FormCard>
        <FormHeader stepIndex={info.index} totalSteps={info.total} label="Settings" />

        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
          Pick who owns this campaign and (for outreach) which mailboxes can send.
          Org-level send windows, daily limits and the marketing domain are managed
          under <Link to="/settings" style={{ color: 'var(--color-text-link)', fontWeight: 'var(--font-weight-semibold)', textDecoration: 'none' }}>org settings</Link>.
        </p>

        <OwnerPicker campaignId={id} currentOwnerId={campaign.owner_user_id} />

        {/* Trigger campaigns can send from the owner of the deal/job that
            triggered each enrollment, instead of the campaign owner. */}
        <SendFromOwnerCard campaignId={id} value={campaign.send_from_owner} />

        {campaign.type !== 'NEWSLETTER' && campaign.type !== 'SMS_BLAST' && (
          <MailboxPicker campaignId={id} initialSelection={campaign.allowed_mailbox_ids ?? []} />
        )}

        {/* SMS number picker — required for SMS_BLAST and shown for any
            outreach type since the sequence might include SMS steps. Under
            Dialpad the sender is the campaign owner's linked number, so there's
            no per-campaign number to pick. */}
        {campaign.type !== 'NEWSLETTER' && activeSmsProvider !== 'DIALPAD' && (
          <SmsNumberPicker campaignId={id} currentNumberId={campaign.sms_number_id ?? null} />
        )}
        {campaign.type !== 'NEWSLETTER' && activeSmsProvider === 'DIALPAD' && (
          <ReadOnlyCard
            label="SMS sender"
            value="Dialpad: the campaign owner's linked number"
            note="Your org sends SMS via Dialpad in JobTetris, so each automation sends from its owner's own Dialpad number, as linked in JobTetris. There's no per-campaign number to pick; sends fall back to the org-default number if the owner isn't linked."
          />
        )}

        {campaign.type === 'NEWSLETTER' && (
          <ReadOnlyCard
            label="Sending channel"
            value="Newsletter (Postmark)"
            note="Routes through the org's primary verified marketing domain. Outreach mailboxes are not used for newsletters."
          />
        )}

        {/* Sprint 16b — per-campaign merge tokens. Renders the manifest-
            driven form when the chosen templates need anything beyond the
            auto-resolved set; renders a quiet "All filled" note otherwise. */}
        <TemplateInputsCard campaign={campaign} />

        <WizardFooter
          onBack={() => navigate(stepPath(id, 'sequence'))}
          onNext={() => navigate(stepPath(id, 'review'))}
          nextLabel="Next: Review and launch"
        />
      </FormCard>
    </WizardLayout>
  );
}

/* ── Owner picker ── */

function OwnerPicker({ campaignId, currentOwnerId }: { campaignId: string; currentOwnerId: string }) {
  const { data: users, isLoading } = useOrgUsers();
  const update = useUpdateCampaign(campaignId);
  const { addToast } = useToast();
  // The owner id may be the user's JobTetris account id if they hadn't signed in yet (JT1-1908),
  // so the picker works on the matching user's jt_user_id.
  const current = useMemo(() => findOwner(currentOwnerId, users), [users, currentOwnerId]);
  const currentValue = current?.jt_user_id ?? currentOwnerId;
  const [value, setValue] = useState(currentValue);

  useEffect(() => { setValue(currentValue); }, [currentValue]);

  const dirty = value !== currentValue;

  const save = async () => {
    try {
      await update.mutateAsync({ owner_user_id: value });
      addToast({ variant: 'success', title: 'Campaign owner updated' });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not update owner', description: (err as Error).message });
    }
  };

  return (
    <div style={cardStyle()}>
      <div style={cardHeaderStyle()}>
        <span style={cardLabelStyle()}>Campaign owner</span>
        <span style={cardNoteStyle()}>
          Determines whose personal mailboxes the picker tries first when no
          allowlist is set. Used as the actor on activity-tab entries.
        </span>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        {isLoading ? (
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Loading users…
          </span>
        ) : (
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={inputStyle()}
          >
            {/* Defensive option so an unknown current owner still renders */}
            {!current && <option value={currentOwnerId}>Someone no longer in the team</option>}
            {users?.map((u) => (
              <option key={u.jt_user_id} value={u.jt_user_id}>
                {u.name} · {u.email}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          style={primaryBtnStyle(!dirty || update.isPending)}
        >
          {update.isPending ? 'Saving…' : 'Save owner'}
        </button>
      </div>
    </div>
  );
}

/* ── Mailbox picker ── */

function MailboxPicker({ campaignId, initialSelection }: { campaignId: string; initialSelection: string[] }) {
  const { data, isLoading } = useSmtpMailboxes();
  const mailboxes = data?.data ?? [];
  const filteredInitial = initialSelection.filter((id) => mailboxes.some((m) => m.id === id));
  const [selected, setSelected] = useState<string[]>(filteredInitial);
  const [snapshot, setSnapshot] = useState<string[]>(filteredInitial);
  const update = useUpdateCampaignMailboxes(campaignId);
  const { addToast } = useToast();

  useEffect(() => {
    // Refresh state when the list of mailboxes loads in (initial empty mailbox
    // list means filteredInitial is also empty — sync up once data arrives).
    if (data && snapshot.length === 0 && initialSelection.length > 0) {
      const next = initialSelection.filter((id) => mailboxes.some((m) => m.id === id));
      setSelected(next);
      setSnapshot(next);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const dirty = !sameSet(selected, snapshot);
  const toggle = (id: string) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    try {
      await update.mutateAsync({ allowed_mailbox_ids: selected.length === 0 ? null : selected });
      setSnapshot(selected);
      addToast({
        variant: 'success',
        title: 'Mailbox selection updated',
        description: selected.length === 0
          ? 'No restriction: picker uses default ownership order.'
          : `Restricted to ${selected.length} mailbox${selected.length === 1 ? '' : 'es'}.`,
      });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not update mailboxes', description: (err as Error).message });
    }
  };

  return (
    <div style={cardStyle()}>
      <div style={cardHeaderStyle()}>
        <span style={cardLabelStyle()}>Sending mailboxes (outreach)</span>
        <span style={cardNoteStyle()}>
          Pick one for a locked sender, multiple for a pool, or leave empty to use
          any of the owner&rsquo;s eligible mailboxes. Once a recipient gets their
          first send from a mailbox, future steps stick to the same one.
        </span>
      </div>

      {isLoading ? (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Loading mailboxes…
        </span>
      ) : mailboxes.length === 0 ? (
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No SMTP mailboxes configured yet:{' '}
          <Link to="/settings" style={{ color: 'var(--color-text-link)', textDecoration: 'none', fontWeight: 'var(--font-weight-semibold)' }}>
            add one in Settings &rarr; Mailboxes
          </Link>
          .
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {mailboxes.map((m) => {
            const checked = selected.includes(m.id);
            return (
              <li key={m.id}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--btn-radius)',
                    backgroundColor: checked ? 'var(--color-surface-muted)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggle(m.id)} />
                  <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
                    {m.label}{m.is_shared ? ' · shared' : ''}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {m.email_address}
                  </span>
                  {m.status !== 'ACTIVE' && (
                    <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      ({m.status.toLowerCase()})
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          style={primaryBtnStyle(!dirty || update.isPending)}
        >
          {update.isPending ? 'Saving…' : 'Save mailbox selection'}
        </button>
        {dirty && (
          <button type="button" onClick={() => setSelected(snapshot)} disabled={update.isPending} style={secondaryBtnStyle()}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

/* ── SMS number picker ── */

function SmsNumberPicker({ campaignId, currentNumberId }: { campaignId: string; currentNumberId: string | null }) {
  const { data, isLoading } = useSmsAccounts();
  const accounts = data?.data ?? [];
  // Per-campaign binding is a Twilio-only concept — only list Twilio numbers.
  // (Dialpad routes per campaign owner and this picker isn't shown for it.)
  const numbers = accounts
    .filter((a) => a.provider === 'TWILIO')
    .flatMap((a) => a.numbers.map((n) => ({
      ...n,
      account_label: a.label,
      account_status: a.status,
    })));
  const [value, setValue] = useState<string>(currentNumberId ?? '');
  const update = useUpdateCampaignSmsNumber(campaignId);
  const { addToast } = useToast();

  useEffect(() => { setValue(currentNumberId ?? ''); }, [currentNumberId]);

  const dirty = (value || null) !== currentNumberId;

  const save = async () => {
    try {
      await update.mutateAsync({ sms_number_id: value === '' ? null : value });
      addToast({
        variant: 'success',
        title: 'SMS number updated',
        description: value === '' ? 'No SMS number bound. SMS steps will fail at launch.' : `Bound to ${numbers.find((n) => n.id === value)?.e164 ?? value}.`,
      });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not update SMS number', description: (err as Error).message });
    }
  };

  return (
    <div style={cardStyle()}>
      <div style={cardHeaderStyle()}>
        <span style={cardLabelStyle()}>SMS sender number</span>
        <span style={cardNoteStyle()}>
          One number per campaign. Every SMS goes through it so recipients always
          see the same sender. Required if this sequence includes SMS steps.
        </span>
      </div>

      {isLoading ? (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Loading numbers…
        </span>
      ) : numbers.length === 0 ? (
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          No Twilio numbers available:{' '}
          <Link to="/settings" style={{ color: 'var(--color-text-link)', textDecoration: 'none', fontWeight: 'var(--font-weight-semibold)' }}>
            connect a Twilio account in Settings &rarr; Twilio (SMS)
          </Link>
          .
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={value} onChange={(e) => setValue(e.target.value)} style={inputStyle()}>
            <option value="">No SMS number</option>
            {numbers.map((n) => (
              <option key={n.id} value={n.id} disabled={n.account_status === 'DISABLED'}>
                {n.e164} · {n.account_label} ({n.country_code})
                {n.account_status === 'REQUIRES_SENDER_ID' && n.country_code === 'AU' ? ' · AU gated' : ''}
                {n.account_status === 'DISABLED' ? ' · disabled' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || update.isPending}
            style={primaryBtnStyle(!dirty || update.isPending)}
          >
            {update.isPending ? 'Saving…' : 'Save SMS number'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Send-from-owner (per-campaign override) ── */

function SendFromOwnerCard({ campaignId, value }: { campaignId: string; value?: boolean | null }) {
  const update = useUpdateCampaign(campaignId);
  const { addToast } = useToast();
  const { data: org } = useOrgSettings();
  const orgDefault = org?.data?.send_from_owner_default ?? false;
  const sel = value === null || value === undefined ? 'default' : value ? 'on' : 'off';

  const onChange = async (v: string) => {
    const next = v === 'default' ? null : v === 'on';
    try {
      await update.mutateAsync({ send_from_owner: next });
      addToast({ variant: 'success', title: 'Sender routing updated' });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not update', description: (err as Error).message });
    }
  };

  return (
    <div style={cardStyle()}>
      <div style={cardHeaderStyle()}>
        <span style={cardLabelStyle()}>Send from owner mailbox</span>
        <span style={cardNoteStyle()}>
          For trigger campaigns, send each email from the mailbox of the <strong>owner</strong> of the booking link, job
          or deal that started the enrollment, instead of the campaign owner. If the owner has no connected, active
          mailbox the send is held and your org admins are alerted.
        </span>
      </div>
      <select value={sel} onChange={(e) => onChange(e.target.value)} style={inputStyle()} disabled={update.isPending}>
        <option value="default">Use org default ({orgDefault ? 'On' : 'Off'})</option>
        <option value="on">On: send from the booking link, job or deal owner</option>
        <option value="off">Off: send from the campaign owner</option>
      </select>
    </div>
  );
}

/* ── Helpers ── */

function ReadOnlyCard({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div style={cardStyle()}>
      <div style={cardHeaderStyle()}>
        <span style={cardLabelStyle()}>{label}</span>
        <span style={cardNoteStyle()}>{note}</span>
      </div>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-secondary)' }}>
        {value}
      </span>
    </div>
  );
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function cardStyle(): React.CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-3)',
    padding: 'var(--space-4)',
    borderRadius: 'var(--radius-input)',
    backgroundColor: 'var(--color-surface-muted)',
    border: '1px solid var(--color-border-default)',
  };
}

function cardHeaderStyle(): React.CSSProperties {
  return { display: 'flex', flexDirection: 'column', gap: '2px' };
}

function cardLabelStyle(): React.CSSProperties {
  return {
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: 'var(--tracking-caps)',
  };
}

function cardNoteStyle(): React.CSSProperties {
  return {
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-muted)',
    lineHeight: 'var(--leading-relaxed)',
  };
}

function inputStyle(): React.CSSProperties {
  return {
    height: 'var(--btn-height)',
    padding: '0 var(--space-3)',
    borderRadius: 'var(--radius-input)',
    border: '1px solid var(--color-border-default)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-sm)',
    backgroundColor: 'var(--color-surface-white)',
    minWidth: '260px',
  };
}

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    height: 'var(--btn-height)',
    padding: '0 var(--btn-padding-x)',
    borderRadius: 'var(--btn-radius)',
    backgroundColor: disabled ? 'var(--color-surface-muted)' : 'var(--color-action-primary)',
    color: disabled ? 'var(--color-text-muted)' : 'var(--color-action-primary-text)',
    border: 'none',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--btn-font-size)',
    fontWeight: 'var(--btn-font-weight)',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function secondaryBtnStyle(): React.CSSProperties {
  return {
    height: 'var(--btn-height)',
    padding: '0 var(--btn-padding-x)',
    borderRadius: 'var(--btn-radius)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)',
    border: '1px solid var(--color-border-default)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--btn-font-size)',
    cursor: 'pointer',
  };
}

function LoadingShell() {
  return (
    <WizardLayout step="settings">
      <FormCard>
        <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-muted)' }}>Loading campaign…</p>
      </FormCard>
    </WizardLayout>
  );
}
